import { GetPaths, useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Text } from "@certn/cascadia";
import {
  Select,
  SelectItem,
  SelectSection,
  SelectSectionHeader,
  Card,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useOrderContext } from "../../../../pages/order/OrderContext";
import { formatCheckPriceLabel } from "../../../../utils/formatCheckPriceLabel";
import { resolveOptionPriceIsCatalogMinimum } from "../../../../utils/resolvePriceIsCatalogMinimum";
import { OrderingCheckListChild } from "../../types";
import { SelectFieldDescription } from "./SelectFieldDescription";
import { useCanViewClientPortalPricing } from "../../../permissions/Permissions";

import { typesNotToAutoSelectOption } from "../../types";
import { valuesMatch } from "../../utils";
import {
  getCheckDisplayNameFromType,
  isInternationalCriminalCheck,
} from "../learn-more-modal/utils";

const TEMPLATE_ID_SELECT_FIELD = "template_id";

type UmbrellaCheckSelectOption =
  components["schemas"]["UmbrellaCheckSelectOption"];
type CriminalRecordTemplateOptionsList =
  components["schemas"]["CriminalRecordTemplateOptionsList"];

type CheckCardSelectOption = UmbrellaCheckSelectOption &
  Partial<Pick<CriminalRecordTemplateOptionsList, "template_id">>;

function checkArgumentsFromOption(
  option: CheckCardSelectOption,
  selectionParameters: Record<string, unknown> | undefined
): Record<string, unknown> {
  const checkArguments: Record<string, unknown> = {
    ...selectionParameters,
    ordering_type: option.ordering_type,
    [option.select_field_argument]: option.value,
  };
  if (option.select_field_argument !== TEMPLATE_ID_SELECT_FIELD) {
    checkArguments.template_id = option.template_id ?? null;
  }
  return checkArguments;
}

export interface CheckCardSelectProps {
  selectFieldDescription?: string[][];
  selectFieldEndpoint?: string | null;
  selectOptions?: CheckCardSelectOption[];
  onUpdateSelection: (
    checkArguments: Record<string, unknown>,
    selectedOptions?: CheckCardSelectOption[]
  ) => void;
  type: string;
  customerGroup?: string;
  selectedOptions?: CheckCardSelectOption[];
  isDisabled?: boolean;
  checkArguments?: Record<string, unknown>;
  currency?: components["schemas"]["CurrencyEnum"];
  selectFieldInformation?: components["schemas"]["SelectFieldInformationGroup"];
  isApplicantPay?: boolean;
  priceIsCatalogMinimum?: boolean;
  checkChildren?: OrderingCheckListChild[];
}

export function CheckCardSelect(props: CheckCardSelectProps) {
  const {
    selectFieldDescription,
    selectFieldEndpoint,
    selectOptions: selectOptionsProps,
    type,
    onUpdateSelection,
    customerGroup,
    selectedOptions,
    isDisabled,
    checkArguments,
    currency,
    selectFieldInformation,
    isApplicantPay,
    priceIsCatalogMinimum: parentPriceIsCatalogMinimum,
    checkChildren,
  } = props;
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const { setCheckValidationError } = useOrderContext();
  const { data, isLoading } = useClientPortalAPI(
    selectFieldEndpoint ? (selectFieldEndpoint as GetPaths) : null,
    {
      customer_group_id: customerGroup,
      is_applicant_pay: isApplicantPay ? "true" : undefined,
    }
  );

  // - selectedOption.value is unknown, this is because the backend will sometimes return a string, sometimes a number, sometimes a list.
  //    - it gets cast to string when used as a key/id in the select component.
  const [selectOptions, setSelectOptions] = useState<CheckCardSelectOption[]>(
    []
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedOptionDescription, setSelectedOptionDescription] = useState<
    string | null
  >(null);

  const selectionParamsKey = JSON.stringify(
    selectFieldInformation?.selection_parameters
  );

  // Reset selection state when the radio selection changes
  useEffect(() => {
    setSelectedKey(null);
    setSelectedOptionDescription(null);
  }, [selectionParamsKey]);

  const canShowErrors = typesNotToAutoSelectOption.some((notType) =>
    type.includes(notType)
  );

  const items = useMemo(() => {
    if (!selectOptions.length) return [];

    // Sort options: standalone checks first alphabetically, then international checks alphabetically
    const sortedOptions = [...selectOptions].sort((a, b) => {
      // Extract check type from value array (first element)
      const aCheckType = Array.isArray(a.value) ? a.value[0] : String(a.value);
      const bCheckType = Array.isArray(b.value) ? b.value[0] : String(b.value);

      const aIsIntl = isInternationalCriminalCheck(aCheckType);
      const bIsIntl = isInternationalCriminalCheck(bCheckType);

      // Standalone checks come before international checks
      if (!aIsIntl && bIsIntl) return -1;
      if (aIsIntl && !bIsIntl) return 1;

      // Within each group, sort alphabetically by display name
      const aDisplayName = getCheckDisplayNameFromType(
        aCheckType,
        a.label,
        "DROPDOWN"
      );
      const bDisplayName = getCheckDisplayNameFromType(
        bCheckType,
        b.label,
        "DROPDOWN"
      );

      return aDisplayName.localeCompare(bDisplayName);
    });

    return sortedOptions.map((option) => {
      const checkType = Array.isArray(option.value)
        ? option.value[0]
        : String(option.value);
      const displayName = getCheckDisplayNameFromType(
        checkType,
        option.label,
        "DROPDOWN"
      );

      return {
        id: String(option.value),
        value: String(option.value),
        label: displayName,
        price: option.price,
        priceIsCatalogMinimum: resolveOptionPriceIsCatalogMinimum(
          option,
          checkChildren,
          parentPriceIsCatalogMinimum
        ),
      };
    });
  }, [checkChildren, parentPriceIsCatalogMinimum, selectOptions]);

  // Separate "current address" (non-template) options from template options
  // to render them in sections when both are present
  const standaloneOptions = useMemo(
    () =>
      selectOptions.filter(
        (opt) => opt.select_field_argument !== "template_id"
      ),
    [selectOptions]
  );

  const templateOptions = useMemo(
    () =>
      selectOptions.filter(
        (opt) => opt.select_field_argument === "template_id"
      ),
    [selectOptions]
  );

  const hasSections =
    standaloneOptions.length > 0 && templateOptions.length > 0;

  const hasNoOptions = !items?.length;

  const getValidationError = useCallback(() => {
    if (hasNoOptions) {
      return t("No options available.");
    }
    if (selectedKey === null) {
      return t("Please select an option.");
    }
    return null;
  }, [selectedKey, hasNoOptions, t]);

  const getOptionPriceLabel = useCallback(
    (price: unknown, priceIsCatalogMinimum?: boolean) => {
      return formatCheckPriceLabel(price, {
        currency,
        locale: "en-US",
        priceIsCatalogMinimum,
        fromPrefix: t("From "),
      });
    },
    [currency, t]
  );

  const handleOnUpdateSelection = useCallback(
    (
      checkArguments: Record<string, unknown>,
      selectedOptions?: CheckCardSelectOption[]
    ) => {
      onUpdateSelection(checkArguments, selectedOptions);
    },
    [onUpdateSelection]
  );

  useEffect(() => {
    // Capture type at effect time to ensure cleanup clears the correct key
    const currentType = type;

    if (isDisabled) {
      setCheckValidationError(currentType, false);
    } else if (hasNoOptions) {
      setCheckValidationError(currentType, true);
    } else if (canShowErrors) {
      const error = getValidationError();
      setCheckValidationError(currentType, !!error);
    } else {
      setCheckValidationError(currentType, false);
    }

    // Cleanup: clear validation error when component unmounts (check is deselected)
    return () => {
      setCheckValidationError(currentType, false);
    };
  }, [
    isDisabled,
    canShowErrors,
    hasNoOptions,
    getValidationError,
    setCheckValidationError,
    type,
  ]);

  useEffect(() => {
    if (selectOptionsProps?.length) {
      setSelectOptions(selectOptionsProps);
    } else if (data) {
      setSelectOptions(data as unknown as CheckCardSelectOption[]);
    }
  }, [selectOptionsProps, data]);

  useEffect(() => {
    // Set selected option key/description or auto-select first valid option
    if (selectedOptions && selectedOptions.length > 0) {
      setSelectedKey(String(selectedOptions[0].value));
      setSelectedOptionDescription(selectedOptions[0].description);
    } else if (checkArguments && selectOptions.length && selectedKey === null) {
      const matchingOption = selectOptions.find((option) =>
        valuesMatch(
          option.value,
          checkArguments[option.select_field_argument] as unknown
        )
      );

      if (matchingOption) {
        setSelectedKey(String(matchingOption.value));
        setSelectedOptionDescription(matchingOption.description);
      } else if (
        !isDisabled &&
        !typesNotToAutoSelectOption.some((notType) => type.includes(notType))
      ) {
        const firstSelectableOption = selectOptions.find(
          (option) => !option.option_has_ordering_error_for_customer_group
        );
        if (!firstSelectableOption) return;
        setSelectedKey(String(firstSelectableOption.value));
        handleOnUpdateSelection(
          checkArgumentsFromOption(
            firstSelectableOption,
            selectFieldInformation?.selection_parameters
          ),
          [firstSelectableOption]
        );
      }
    } else if (
      selectOptions.length &&
      !isDisabled &&
      !typesNotToAutoSelectOption.some((notType) => type.includes(notType)) &&
      selectedKey === null
    ) {
      const firstSelectableOption = selectOptions.find(
        (option) => !option.option_has_ordering_error_for_customer_group
      );

      if (!firstSelectableOption) return;
      setSelectedKey(String(firstSelectableOption.value));
      handleOnUpdateSelection(
        checkArgumentsFromOption(
          firstSelectableOption,
          selectFieldInformation?.selection_parameters
        ),
        [firstSelectableOption]
      );
    }
  }, [
    selectOptions,
    type,
    selectedOptions,
    isDisabled,
    checkArguments,
    handleOnUpdateSelection,
    selectedKey,
    selectFieldInformation,
  ]);

  return (
    <div className={styles.selectListSection}>
      <Select
        isInvalid={
          !isDisabled &&
          (canShowErrors || hasNoOptions) &&
          getValidationError() !== null
        }
        errors={
          !isDisabled && (canShowErrors || hasNoOptions)
            ? [getValidationError()].filter(Boolean)
            : []
        }
        isDisabled={isLoading || isDisabled}
        isLoading={isLoading}
        isRequired
        {...(!hasSections ? { items } : {})}
        placeholder={
          selectFieldInformation?.select_placeholder ?? t("Select an option")
        }
        emptyMessage={
          selectFieldInformation?.no_results_placeholder ??
          t("No results found")
        }
        testID={`${type}-additional-options`}
        label={selectFieldDescription?.[0]?.[0] || t("Type")}
        value={selectedKey}
        onChange={(value) => {
          const selectedOption = selectOptions.find(
            (option) => String(option.value) === String(value)
          );
          if (!selectedOption) return;

          handleOnUpdateSelection(
            checkArgumentsFromOption(
              selectedOption,
              selectFieldInformation?.selection_parameters
            ),
            [selectedOption]
          );
          setSelectedKey(String(selectedOption.value));
        }}
        info={
          <SelectFieldDescription
            selectFieldDescription={selectFieldDescription}
          />
        }
      >
        {hasSections ? (
          <>
            {standaloneOptions.map((option) => (
              <SelectItem
                id={String(option.value)}
                key={String(option.value)}
                textValue={String(option.label)}
                data-testid={`select-option-${option.value}`}
              >
                {option.label}
              </SelectItem>
            ))}
            <SelectSection>
              <SelectSectionHeader>{t("Templates")}</SelectSectionHeader>
              {templateOptions.map((option) => {
                const optionPriceLabel = getOptionPriceLabel(
                  option.price,
                  resolveOptionPriceIsCatalogMinimum(
                    option,
                    checkChildren,
                    parentPriceIsCatalogMinimum
                  )
                );

                return (
                  <SelectItem
                    id={String(option.value)}
                    key={String(option.value)}
                    textValue={String(option.label)}
                    data-testid={`select-option-${option.value}`}
                  >
                    {canViewClientPortalPricing && optionPriceLabel
                      ? `${option.label} - ${optionPriceLabel}`
                      : option.label}
                  </SelectItem>
                );
              })}
            </SelectSection>
          </>
        ) : (
          (item) => {
            const itemPriceLabel = getOptionPriceLabel(
              item.price,
              item.priceIsCatalogMinimum
            );

            return (
              <SelectItem
                id={String(item.id ?? item.value)}
                key={String(item.id ?? item.value)}
                textValue={String(item.label ?? item.value)}
                data-testid={`select-option-${item.value}`}
              >
                {canViewClientPortalPricing && itemPriceLabel
                  ? `${item.label} - ${itemPriceLabel}`
                  : item.label}
              </SelectItem>
            );
          }
        )}
      </Select>
      {selectedOptionDescription && (
        <Card
          padding="space-4"
          background="background-accent-neutral-weak"
          border="none"
        >
          <Text style="global/heading-small" color="text-base">
            {selectedOptionDescription}
          </Text>
        </Card>
      )}
      {selectFieldDescription &&
        selectFieldDescription[2] &&
        selectFieldDescription[2][0] && (
          <Text style="global/info-label" color="text-weak">
            {selectFieldDescription[2][0]}
          </Text>
        )}
    </div>
  );
}

const styles = {
  selectListSection: css({
    padding: `${"var(--space-2)"} ${"var(--space-3)"} ${"var(--space-3)"}`,
    margin: "var(--space-0) var(--space-05)",
    gap: "var(--space-2)",
    backgroundColor: "var(--background-accent-neutral-weakest)",
    display: "flex",
    flexDirection: "column",
  }),
};
